import { useRequestFeedback } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { confirm } from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import type { GitBookAgentClient } from '../Agent';

/**
 * Button to configure the agent for a change request.
 */
export function AgentChangeRequestControlButton(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    agent: GitBookAgentClient;
}) {
    const { space, changeRequest, agent } = props;
    const { navigate } = useNavigation();

    const requestFeedback = useRequestFeedback();

    return (
        <PaletteButton
            kind="blank"
            label="Agent options"
            leadingIcon={Icon.MoreVertical}
            iconOnly
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'options',
                            commands: [
                                {
                                    key: 'reset',
                                    icon: Icon.Trashcan,
                                    title: 'Reset conversation',
                                    trigger: () => {
                                        void confirm({
                                            title: 'Reset conversation',
                                            description: (
                                                <>
                                                    This will reset the conversation with the agent
                                                    and start a new one. All messages will be lost.
                                                </>
                                            ),
                                            kind: 'danger',
                                            confirmLabel: 'Reset',
                                            onConfirm: () => {
                                                agent.reset();

                                                analytics.logEvent(
                                                    analytics.createChangeRequestEvent(
                                                        space,
                                                        changeRequest.id,
                                                        {
                                                            type: 'change_request_agent_reset',
                                                            messagesCount:
                                                                agent.state.messages.length,
                                                        }
                                                    )
                                                );
                                            },
                                        });
                                    },
                                },
                                {
                                    key: 'configure',
                                    icon: Icon.Settings,
                                    title: 'Configure Docs Agent',
                                    trigger: () => {
                                        navigate({
                                            screen: {
                                                type: 'org_settings',
                                                tab: { type: 'agents' },
                                            },
                                        });
                                    },
                                },
                            ],
                        },
                        {
                            key: 'feedback',
                            commands: [
                                {
                                    key: 'feedback',
                                    icon: Icon.Happy,
                                    title: 'Share feedback',
                                    trigger: () => {
                                        requestFeedback({
                                            flagKey: 'DOCS_AGENTS',
                                            title: 'How satisfied are you with using Docs Agents in change requests?',
                                            openWithCommentVisible: true,
                                        });
                                    },
                                },
                            ],
                        },
                    ],
                })
            }
        />
    );
}
